/* eslint-disable no-promise-executor-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosResponse } from 'axios';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';

import api from '../../api/api';
import { errorHandling } from '../../api/errorHandling';

import {
 getConfigStorageItem,
 setConfigStorageItem,
} from '../../hooks/useConfigStorage';
import { isJson } from '../../utils/dataTypes';
import { RootState } from '../store';
import { ClientConfigurationItem } from './clientConfigurationSlice';
import {
 createClientConfigurationFailure,
 createClientConfigurationFinish,
 createClientConfigurationStart,
 createClientConfigurationSuccess,
 deleteClientConfigurationFailure,
 deleteClientConfigurationFinish,
 deleteClientConfigurationStart,
 deleteClientConfigurationSuccess,
 getClientConfigurationFailure,
 getClientConfigurationFinish,
 getClientConfigurationSelfFailure,
 getClientConfigurationSelfStart,
 getClientConfigurationSelfSuccess,
 getClientConfigurationsFailure,
 getClientConfigurationsFinish,
 getClientConfigurationsStart,
 getClientConfigurationsSuccess,
 getClientConfigurationStart,
 getClientConfigurationSuccess,
 updateClientConfigurationFailure,
 updateClientConfigurationFinish,
 updateClientConfigurationStart,
 updateClientConfigurationSuccess,
} from './clientConfigurationSlice';

const getConfigByName = (name: string, configurationObj: any) => {
 if (typeof configurationObj !== 'object' || configurationObj === null)
  return false;

 const confVal = configurationObj.find(
  (conf: any) => conf.Parameter === name,
 )?.Value;
 //  console.log({ confVal, a: JSON.parse(confVal), name, configurationObj });
 if (isJson(confVal)) {
  return JSON.parse(confVal);
 }
 return false;
};

const mergeClientConfigurationItem = (
 list: ClientConfigurationItem[] | null | undefined,
 item: ClientConfigurationItem | null | undefined,
): ClientConfigurationItem[] => {
 const current = Array.isArray(list) ? list : [];
 if (!item || typeof item !== 'object') return current;
 const byId = item.Id
  ? current.findIndex((c) => c.Id === item.Id)
  : -1;
 if (byId >= 0) {
  const next = current.slice();
  next[byId] = { ...current[byId], ...item };
  return next;
 }
 const byParameter = item.Parameter
  ? current.findIndex((c) => c.Parameter === item.Parameter)
  : -1;
 if (byParameter >= 0) {
  const next = current.slice();
  next[byParameter] = { ...current[byParameter], ...item };
  return next;
 }
 return [...current, item];
};

function* fetchClientConfiguration(props: any) {
 try {
  const response: AxiosResponse<any[]> = yield call(
   api.get,
   `/ClientConfiguration/v1/${props.payload}`,
  );
  yield put(
   getClientConfigurationSuccess({
    data: response.data || [],
    rowCount: response.headers['x-total-count'] || 123,
    // Left untranslated on purpose: none of this slice's Success actions are
    // in src/redux/toast/watchList.ts, so these strings never render. Add a
    // toasts.* key and i18n.t() here if one is ever watched.
    message: 'ClientConfiguration loaded successfully',
    type: 'success',
   }),
  );
  yield delay(0); // ???
  yield put(getClientConfigurationFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, getClientConfigurationFailure);
 }
}

function* fetchClientConfigurations(props: any) {
 try {
  const response: AxiosResponse<any[]> = yield call(
   api.post,
   `/ClientConfiguration/v1`,
   {},
  );
  yield put(
   getClientConfigurationsSuccess({
    message: 'ClientConfiguration loaded successfully',
    type: 'success',
    clientConfigurations: response.data || [],
    data: response.data || [],
    rowCount: response.headers['x-total-count'] || 123,
   }),
  );
  yield delay(0);
  yield put(getClientConfigurationsFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, getClientConfigurationsFailure);
 }
}

const readCachedConfiguration = (): ClientConfigurationItem[] => {
 const raw = getConfigStorageItem('configuration');
 if (Array.isArray(raw)) return raw;
 if (typeof raw === 'string') {
  try {
   const parsed = JSON.parse(raw);
   return Array.isArray(parsed) ? parsed : [];
  } catch {
   return [];
  }
 }
 return [];
};

const writeCachedConfiguration = (list: ClientConfigurationItem[]) => {
 setConfigStorageItem({ key: 'configuration', value: JSON.stringify(list) });
};

function* fetchClientConfigurationSelf(props: any) {
 try {
  const configurationObj = readCachedConfiguration();
  if (!props.payload.cached || configurationObj.length === 0) {
   const response: AxiosResponse<any[]> = yield call(
    api.get,
    `/ClientConfiguration/v1/self`,
   );
   yield put(
    getClientConfigurationSelfSuccess({
     message: 'ClientConfiguration loaded successfully',
     type: 'success',
     clientConfigurations: response.data || [],
     presetsColumns: getConfigByName('columns', response.data),
     fieldPresets: getConfigByName('fields', response.data),
     data: response.data || [],
     rowCount: response.headers['x-total-count'] || 123,
    }),
   );
   writeCachedConfiguration((response.data || []) as ClientConfigurationItem[]);
  } else {
   yield put(
    getClientConfigurationSelfSuccess({
     message: 'ClientConfiguration loaded successfully',
     type: 'success',
     clientConfigurations: configurationObj,
     presetsColumns: getConfigByName('columns', configurationObj),
     fieldPresets: getConfigByName('fields', configurationObj),
     data: configurationObj,
     rowCount: configurationObj.length,
    }),
   );
  }
 } catch (error: unknown) {
  yield* errorHandling(error, getClientConfigurationSelfFailure);
 }
}
function* createClientConfiguration(props: any) {
 try {
  const response: AxiosResponse<ClientConfigurationItem> = yield call(
   api.post,
   `/ClientConfiguration/v1/create`,
   props.payload.data,
  );

  const currentList: ClientConfigurationItem[] | null = yield select(
   (state: RootState) => state.clientConfiguration.clientConfigurations,
  );
  const nextList = mergeClientConfigurationItem(currentList, response.data);

  writeCachedConfiguration(nextList);

  yield put(
   createClientConfigurationSuccess({
    data: response.data,
    clientConfigurations: nextList,
    presetsColumns: getConfigByName('columns', nextList),
    fieldPresets: getConfigByName('fields', nextList),
   }),
  );
  yield delay(0);
  yield put(createClientConfigurationFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, createClientConfigurationFailure);
 }
}

function* updateClientConfiguration(props: any) {
 try {
  const response: AxiosResponse<ClientConfigurationItem> = yield call(
   api.put,
   `/ClientConfiguration/v1/update/${props.payload.id}`,
   props.payload.data,
  );

  const currentList: ClientConfigurationItem[] | null = yield select(
   (state: RootState) => state.clientConfiguration.clientConfigurations,
  );
  const fromResponse =
   response.data &&
   typeof response.data === 'object' &&
   (response.data as ClientConfigurationItem).Parameter
    ? (response.data as ClientConfigurationItem)
    : null;
  const nextItem: ClientConfigurationItem =
   fromResponse ||
   ({
    Id: props.payload.id,
    ...props.payload.data,
   } as ClientConfigurationItem);
  const nextList = mergeClientConfigurationItem(currentList, nextItem);

  writeCachedConfiguration(nextList);

  yield put(
   updateClientConfigurationSuccess({
    data: response.data,
    clientConfigurations: nextList,
    presetsColumns: getConfigByName('columns', nextList),
    fieldPresets: getConfigByName('fields', nextList),
   }),
  );
  yield delay(0);
  yield put(updateClientConfigurationFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, updateClientConfigurationFailure);
 }
}

function* deleteClientConfiguration(props: any) {
 try {
  const response: AxiosResponse<any[]> = yield call(
   api.del,
   `/ClientConfiguration/v1/delete/${props.payload.id}`,
   props.payload,
  );

  yield put(
   deleteClientConfigurationSuccess({
    data: response.data,
    // rowCount: response.headers['x-total-count'] || 123,
   }),
  );
  yield delay(0); // ???
  yield put(deleteClientConfigurationFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, deleteClientConfigurationFailure);
 }
}

export default function* watchClientConfigurationSaga() {
 yield takeLatest(getClientConfigurationStart.type, fetchClientConfiguration);
 yield takeLatest(getClientConfigurationsStart.type, fetchClientConfigurations);
 yield takeLatest(
  getClientConfigurationSelfStart.type,
  fetchClientConfigurationSelf,
 );
 yield takeLatest(
  createClientConfigurationStart.type,
  createClientConfiguration,
 );
 yield takeLatest(
  updateClientConfigurationStart.type,
  updateClientConfiguration,
 );
 yield takeLatest(
  deleteClientConfigurationStart.type,
  deleteClientConfiguration,
 );
}
